import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Plus, Search, Target } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import BehaviorCard from "@/components/behaviors/BehaviorCard";
import CreateBehaviorModal from "@/components/behaviors/CreateBehaviorModal";
import ExploreTemplatesModal from "@/components/behaviors/ExploreTemplatesModal";
import { Behavior } from "@/types/behavior.types";
import { useAuth } from "@/contexts/AuthContext";
import SectionHeader from "../agent/shared/SectionHeader";
import { VoiceAgent } from "@/services/voiceAgentService";

interface VoiceAgentBehaviorsSectionProps {
  agent: VoiceAgent;
  updateAgent: (data: Partial<VoiceAgent>) => Promise<boolean>;
}

const VoiceAgentBehaviorsSection = ({ agent, updateAgent }: VoiceAgentBehaviorsSectionProps) => {
  const [behaviors, setBehaviors] = useState<Behavior[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);
  const [editingBehavior, setEditingBehavior] = useState<Behavior | null>(null);
  const { toast } = useToast();
  const { organization } = useAuth();

  useEffect(() => {
    fetchBehaviors();
  }, [agent.id]);

  const fetchBehaviors = async () => {
    if (!agent.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("behaviors")
        .select("*")
        .eq("agent_id", agent.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setBehaviors((data || []) as Behavior[]);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error fetching behaviors",
        description: error.message || "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (behavior: Behavior | null = null) => {
    setEditingBehavior(behavior);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBehavior(null);
  };

  const handleBehaviorCreated = (newBehavior: Behavior) => {
    setBehaviors((prev) => [newBehavior, ...prev]);
    handleCloseModal();
  };

  const handleBehaviorUpdated = (updatedBehavior: Behavior) => {
    setBehaviors((prev) =>
      prev.map((behavior) =>
        behavior.id === updatedBehavior.id ? updatedBehavior : behavior,
      ),
    );
    handleCloseModal();
  };

  const handleBehaviorDeleted = (behaviorId: string) => {
    setBehaviors((prev) =>
      prev.filter((behavior) => behavior.id !== behaviorId),
    );
  };

  const handleBehaviorAddedFromTemplate = (newBehavior: Behavior) => {
    setBehaviors((prev) => [newBehavior, ...prev]);
    setIsTemplatesModalOpen(false);
  };

  return (
    <>
      <Card className="border rounded-2xl">
        <CardContent className="p-5">
          <SectionHeader
            icon={<Target className="h-5 w-5" />}
            title="Behaviors"
            description="Help your voice agent respond consistently in different phone call situations"
          />

          <div className="flex justify-between items-center mb-6 mt-4">
            <div></div>
            <div className="flex gap-2">
              <Button
                onClick={() => setIsTemplatesModalOpen(true)}
                variant="outline"
                size="sm"
              >
                <Search className="mr-2 h-4 w-4" /> Explore Templates
              </Button>
              <Button onClick={() => handleOpenModal()} size="sm">
                <Plus className="mr-2 h-4 w-4" /> Add Behavior
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="shadow-sm animate-pulse w-full">
                  <CardContent className="h-16"></CardContent>
                </Card>
              ))}
            </div>
          ) : behaviors.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center p-8 border border-dashed rounded-2xl bg-background">
              <div className="h-20 w-20 rounded-full flex items-center justify-center bg-muted mb-6">
                <Plus className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">
                No behaviors defined for this voice agent
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Behaviors help your voice agent handle phone calls consistently
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setIsTemplatesModalOpen(true)}
                  variant="outline"
                >
                  <Search className="mr-2 h-4 w-4" /> Explore Templates
                </Button>
                <Button onClick={() => handleOpenModal()}>
                  <Plus className="mr-2 h-4 w-4" /> Add Behavior
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 w-full">
              {behaviors.map((behavior) => (
                <BehaviorCard
                  key={behavior.id}
                  behavior={behavior}
                  onEdit={() => handleOpenModal(behavior)}
                  onDelete={handleBehaviorDeleted}
                  onUpdate={handleBehaviorUpdated}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateBehaviorModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        behavior={editingBehavior}
        agentId={agent.id}
        onBehaviorCreated={handleBehaviorCreated}
        onBehaviorUpdated={handleBehaviorUpdated}
      />

      {organization && (
        <ExploreTemplatesModal
          isOpen={isTemplatesModalOpen}
          onClose={() => setIsTemplatesModalOpen(false)}
          agentId={agent.id}
          organizationId={organization.id}
          onBehaviorAdded={handleBehaviorAddedFromTemplate}
        />
      )}
    </>
  );
};

export default VoiceAgentBehaviorsSection; 